/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

/** One row of the task list: an Activiti task plus the document it is about. */
export interface TaskRow {
  id: string;
  processId: string;
  name: string;
  description: string;
  step: string;
  priority: number;
  state: string;
  assignee: string;
  startedAt?: string;
  endedAt?: string;
  dueAt?: string;
  processDefinitionId?: string;
  /** The BPMN activity id — tells a user task from a service step. */
  taskKey?: string;
  /** Users/groups allowed to claim an unclaimed task. */
  candidateUsers?: string[];
  candidateGroups?: string[];
  /** Attached documents (first one also surfaced as docId/docName/…). */
  docId?: string;
  docName?: string;
  docMimeType?: string;
  docSize?: number;
  /** dms:documentCode of the attached document, when the model has one. */
  docCode?: string;
}

/** The work kind a row represents — drives the My Work tabs and actions. */
export type TaskKind = 'approve' | 'review' | 'task';

/** The four lists My Work reads, each on its own. */
export type InboxPart = 'todo' | 'pooled' | 'running' | 'done';

/** Colored file-tile class for a document name — shared by the work screens. */
export function fileKindClass(name: string | undefined): string {
  const ext = (name ?? '').split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') {
    return 'aca-file--pdf';
  }
  if (['doc', 'docx', 'odt', 'rtf', 'txt'].includes(ext)) {
    return 'aca-file--doc';
  }
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) {
    return 'aca-file--xls';
  }
  if (['ppt', 'pptx', 'odp'].includes(ext)) {
    return 'aca-file--ppt';
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tif', 'tiff'].includes(ext)) {
    return 'aca-file--img';
  }
  return 'aca-file--any';
}

/** The material icon matching {@link fileKindClass}. */
export function fileKindIcon(name: string | undefined): string {
  switch (fileKindClass(name)) {
    case 'aca-file--pdf':
      return 'picture_as_pdf';
    case 'aca-file--doc':
      return 'article';
    case 'aca-file--xls':
      return 'grid_on';
    case 'aca-file--ppt':
      return 'slideshow';
    case 'aca-file--img':
      return 'image';
    default:
      return 'insert_drive_file';
  }
}

export interface TaskCounts {
  todo: number;
  running: number;
  done: number;
}

export type TaskTab = 'todo' | 'running' | 'done';

/** One entry of a task's history, read from the process's completed tasks. */
export interface TaskHistoryEntry {
  at?: string;
  name: string;
  outcome?: string;
  by?: string;
}

/**
 * Reads the workflow screens' data from Alfresco's embedded Activiti engine.
 *
 * The split with `WorkflowService`: this one reads and shapes lists — the
 * inbox, a task with its documents, a process's history — while
 * `WorkflowService` acts (start, complete, claim, reassign), owns the template
 * catalogue and talks to the jobs sidecar about template flows.
 */
@Injectable({ providedIn: 'root' })
export class MyTasksService {
  private readonly api = inject(AlfrescoApiService);

  /**
   * The workflow API sits beside the core one, so it is reached through the
   * discovery client, whose base path is `{ecm}/alfresco/api` — the same route
   * `WorkflowService` uses, and the one that carries the session's ticket.
   */
  private call<T>(path: string, method = 'GET', body?: any): Observable<T> {
    const instance: any = this.api.getInstance();
    return from(
      instance.discoveryClient.callApi(
        `/-default-/public/workflow/versions/1${path}`,
        method,
        {},
        {},
        {},
        {},
        body ?? null,
        ['application/json'],
        ['application/json']
      ) as Promise<T>
    );
  }

  private me: string | undefined;

  /** The signed-in user's id — the workflow API's filters need it, not `-me-`. */
  private resolveUser(): Observable<string> {
    if (this.me) {
      return of(this.me);
    }
    const instance: any = this.api.getInstance();
    return from(
      instance.contentClient.callApi('/people/-me-', 'GET', {}, {}, {}, {}, null, ['application/json'], ['application/json']) as Promise<any>
    ).pipe(
      map((r: any) => {
        this.me = r?.entry?.id ?? '';
        return this.me;
      })
    );
  }

  /** Tasks assigned to the signed-in user that are still open. */
  todo(): Observable<TaskRow[]> {
    return this.todoOrFail().pipe(catchError(() => of([])));
  }

  /** Unclaimed tasks the signed-in user is a candidate for — the claimable pool. */
  pooled(): Observable<TaskRow[]> {
    return this.pooledOrFail().pipe(catchError(() => of([])));
  }

  /**
   * Everything My Work lists, with a flag for any part that could not be read.
   * The widgets elsewhere treat a failure as "nothing to show"; this page says
   * so instead, because "you have no tasks" is a different answer from "the
   * task list could not be read".
   */
  inbox(): Observable<{ todo: TaskRow[]; pooled: TaskRow[]; running: TaskRow[]; done: TaskRow[]; failed: boolean; failedParts: InboxPart[] }> {
    const failedParts: InboxPart[] = [];
    // each list is read on its own, so one the engine refuses — the finished
    // flows, when a removed definition breaks its history — costs only that list
    const safe = (part: InboxPart, source: Observable<TaskRow[]>) =>
      source.pipe(
        catchError(() => {
          failedParts.push(part);
          return of([] as TaskRow[]);
        })
      );
    return forkJoin({
      todo: safe('todo', this.todoOrFail()),
      pooled: safe('pooled', this.pooledOrFail()),
      running: safe('running', this.processRows('active')),
      done: safe('done', this.processRows('completed'))
    }).pipe(map((lists) => ({ ...lists, failed: failedParts.length > 0, failedParts })));
  }

  private todoOrFail(): Observable<TaskRow[]> {
    return this.resolveUser().pipe(
      switchMap((me) => this.call<any>(`/tasks?where=(assignee='${me}')and(status=active)&maxItems=200`)),
      map((paging) => (paging?.list?.entries ?? []).map((entry: any) => this.toRow(entry.entry))),
      switchMap((rows: TaskRow[]) => this.attachDocuments(rows))
    );
  }

  private pooledOrFail(): Observable<TaskRow[]> {
    return this.resolveUser().pipe(
      switchMap((me) => this.call<any>(`/tasks?where=(candidateUser='${me}')and(status=active)&maxItems=200`)),
      map((paging) => (paging?.list?.entries ?? []).map((entry: any) => this.toRow(entry.entry))),
      switchMap((rows: TaskRow[]) => this.attachDocuments(rows)),
      // engine may return assigned rows too; keep only ones not yet claimed by me
      map((rows) => rows.filter((r) => r.state === 'unclaimed' || !r.assignee))
    );
  }

  /** A single task with its documents — the task-detail page's data. */
  taskDetail(taskId: string): Observable<TaskRow | undefined> {
    return this.call<any>(`/tasks/${taskId}`).pipe(
      map((res) => this.toRow(res?.entry)),
      switchMap((row) => this.attachDocuments([row])),
      map((rows) => rows[0])
    );
  }

  /** The kind of work a task is — review flows approve/reject, adhoc just completes. */
  kindOf(row: Pick<TaskRow, 'processDefinitionId' | 'taskKey' | 'name'>): TaskKind {
    const key = `${row.processDefinitionId ?? ''} ${row.taskKey ?? ''} ${row.name ?? ''}`.toLowerCase();
    if (/parallelgroupreview|groupreview|pooled/.test(key)) {
      return 'approve';
    }
    if (/review/.test(key)) {
      return 'review';
    }
    if (/approve|approval|อนุมัติ/.test(key)) {
      return 'approve';
    }
    return 'task';
  }

  /** Workflows the user started that are still running, as one row each. */
  running(): Observable<TaskRow[]> {
    return this.processRows('active').pipe(catchError(() => of([])));
  }

  /** Workflows the user started that have finished. */
  done(): Observable<TaskRow[]> {
    return this.processRows('completed').pipe(catchError(() => of([])));
  }

  counts(): Observable<TaskCounts> {
    return forkJoin({
      todo: this.total('/tasks?where=(status=active)&maxItems=1'),
      running: this.total('/processes?where=(status=active)&maxItems=1'),
      done: this.total('/processes?where=(status=completed)&maxItems=1')
    });
  }

  /**
   * A task's history: the tasks already completed on the same process. The
   * engine keeps no separate audit of a task, so this is the honest source.
   */
  history(processId: string): Observable<TaskHistoryEntry[]> {
    return this.call<any>(`/processes/${processId}/tasks?where=(status=any)&maxItems=50`).pipe(
      map((paging) =>
        (paging?.list?.entries ?? [])
          .map((entry: any) => entry.entry)
          .map((task: any) => ({
            at: task.endedAt || task.startedAt,
            name: task.name,
            outcome: task.state,
            by: task.assignee
          }))
      ),
      catchError(() => of([]))
    );
  }

  private total(path: string): Observable<number> {
    return this.call<any>(path).pipe(
      map((paging) => paging?.list?.pagination?.totalItems ?? 0),
      catchError(() => of(0))
    );
  }

  /**
   * Processes the signed-in user started. The API answers with every process
   * the caller may see — for an administrator, everyone's — so the starter is
   * checked here rather than trusting the list to be "mine".
   */
  private processRows(status: 'active' | 'completed'): Observable<TaskRow[]> {
    return this.resolveUser().pipe(
      switchMap((me) =>
        this.call<any>(`/processes?where=(status=${status})&maxItems=200`).pipe(
          map((paging) => (paging?.list?.entries ?? []).filter((entry: any) => entry?.entry?.startUserId === me))
        )
      ),
      map((entries: any[]) =>
        entries.map((entry: any) => {
          const process = entry.entry;
          return {
            id: process.id,
            processId: process.id,
            name: process.processDefinitionId?.split(':')[0] ?? 'workflow',
            description: '',
            step: status === 'completed' ? 'DMS.UI.1ED711' : 'DMS.UI.3B437F',
            priority: 2,
            state: status,
            assignee: process.startUserId,
            startedAt: process.startedAt,
            dueAt: process.dueAt,
            processDefinitionId: process.processDefinitionId
          } as TaskRow;
        })
      ),
      switchMap((rows: TaskRow[]) => this.attachDocuments(rows, 'process'))
    );
  }

  private toRow(task: any): TaskRow {
    if (!task) {
      return undefined as unknown as TaskRow;
    }
    const candidateUsers: string[] = (task.candidateUsers ?? []).map((c: any) => c?.id ?? c);
    const candidateGroups: string[] = (task.candidateGroups ?? []).map((c: any) => c?.id ?? c);
    return {
      id: task.id,
      processId: task.processId,
      name: task.name,
      description: task.description ?? '',
      step: task.name,
      priority: task.priority ?? 2,
      state: task.state,
      assignee: task.assignee,
      startedAt: task.startedAt,
      endedAt: task.endedAt,
      dueAt: task.dueAt,
      processDefinitionId: task.processDefinitionId,
      taskKey: task.activityDefinitionId,
      candidateUsers,
      candidateGroups
    };
  }

  /**
   * Reads each row's attached document. One request per row, which is what the
   * API offers — there is no bulk form — so the list is capped by the caller.
   */
  private attachDocuments(rows: TaskRow[], source: 'task' | 'process' = 'task'): Observable<TaskRow[]> {
    if (!rows.length) {
      return of(rows);
    }
    return forkJoin(
      rows.map((row) =>
        this.call<any>(`/${source === 'task' ? 'tasks' : 'processes'}/${source === 'task' ? row.id : row.processId}/items`).pipe(
          map((paging) => {
            const item = paging?.list?.entries?.[0]?.entry;
            return item ? { ...row, docId: item.id, docName: item.name, docMimeType: item.mimeType, docSize: item.size } : row;
          }),
          switchMap((r) => this.attachDocCode(r)),
          catchError(() => of(row))
        )
      )
    );
  }

  /** The node's full property bag (dms:* metadata) for the document info card. */
  nodeProps(nodeId: string): Observable<Record<string, any>> {
    const instance: any = this.api.getInstance();
    return from(
      instance.contentClient.callApi(
        `/nodes/${nodeId}`,
        'GET',
        {},
        { include: 'properties' },
        {},
        {},
        null,
        ['application/json'],
        ['application/json']
      ) as Promise<any>
    ).pipe(
      map((res: any) => res?.entry?.properties ?? {}),
      catchError(() => of({} as Record<string, any>))
    );
  }

  /** The folder holding the node — where task attachments are uploaded. */
  parentOf(nodeId: string): Observable<string> {
    const instance: any = this.api.getInstance();
    return from(
      instance.contentClient.callApi(
        `/nodes/${nodeId}`,
        'GET',
        {},
        { include: 'path' },
        {},
        {},
        null,
        ['application/json'],
        ['application/json']
      ) as Promise<any>
    ).pipe(
      map((res: any) => {
        const els: any[] = res?.entry?.path?.elements ?? [];
        return els.length ? els[els.length - 1].id : '-my-';
      }),
      catchError(() => of('-my-'))
    );
  }

  /** The doc's dms:documentCode — one nodes call, skipped when there is no doc. */
  private attachDocCode(row: TaskRow): Observable<TaskRow> {
    if (!row.docId) {
      return of(row);
    }
    const instance: any = this.api.getInstance();
    return from(
      instance.contentClient.callApi(
        `/nodes/${row.docId}`,
        'GET',
        {},
        { include: 'properties' },
        {},
        {},
        null,
        ['application/json'],
        ['application/json']
      ) as Promise<any>
    ).pipe(
      map((res: any) => ({ ...row, docCode: res?.entry?.properties?.['dms:documentCode'] ?? '' })),
      catchError(() => of(row))
    );
  }
}
